import { useEffect, useRef, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useChat } from "@/context/ChatContext";
import { Header } from "@/components/Header";
import { ManualeButton } from "@/components/ManualeButton";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import {
  Users, Send, AlertTriangle, CheckCircle2, Clock, Hash, Circle, Loader2, Bell, BellOff, Smile, SmilePlus,
} from "lucide-react";

const REACTION_EMOJIS = ["👍", "❤️", "😂", "🎉", "👀", "🙏"];

const EMOJIS = [
  "😀", "😁", "😂", "🤣", "😊", "😇", "🙂", "😉", "😍", "😘",
  "😎", "🤩", "🥳", "😌", "😔", "😴", "🤔", "🤨", "😐", "😬",
  "🙄", "😮", "😲", "😳", "😭", "😤", "😡", "🤯", "😱", "🥺",
  "👍", "👎", "👌", "👏", "🙌", "🙏", "💪", "🤝", "✌️", "🤞",
  "❤️", "🔥", "⭐", "✅", "❌", "⚠️", "❓", "❗", "💡", "📌",
  "📎", "📅", "⏰", "💰", "📞", "📧", "📝", "🎉", "🚀", "✨",
];

const AMBITO_LABEL = { contabilita: "Contabilità", polizze: "Emissione polizze", altro: "Altro" };
const PRIO_META = {
  alta: { label: "Alta", dot: "bg-red-500", ring: "border-red-300 bg-red-50" },
  media: { label: "Media", dot: "bg-amber-500", ring: "border-amber-300 bg-amber-50" },
  normale: { label: "Servizio", dot: "bg-slate-400", ring: "border-slate-200 bg-white" },
};

function timeLabel(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  return d.toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
}

function MessageBubble({ m, me, onStatus, busyId, onReact }) {
  const mine = m.sender_id === me;
  const prio = m.priority || "normale";
  const isAlta = prio === "alta";
  const open = isAlta && m.status === "aperta";
  const working = isAlta && m.status === "in_lavorazione";
  const done = isAlta && m.status === "lavorata";

  let cardCls = "border-slate-200 bg-white";
  if (open) cardCls = "border-red-400 bg-red-50 ring-2 ring-red-200 animate-pulse";
  else if (working) cardCls = "border-amber-300 bg-amber-50";
  else if (done) cardCls = "border-emerald-200 bg-emerald-50";
  else if (prio === "media") cardCls = "border-amber-200 bg-amber-50/60";

  return (
    <div className={`flex ${mine ? "justify-end" : "justify-start"}`} data-testid={`chat-msg-${m.id}`}>
      <div className={`max-w-[78%] rounded-xl border px-3.5 py-2.5 ${cardCls}`}>
        <div className="flex items-center gap-2 mb-1">
          <span className="text-xs font-semibold text-slate-700">{mine ? "Tu" : m.sender_name}</span>
          {isAlta && <Badge className="bg-red-600 text-white border-0 text-[10px] px-1.5 py-0">ALTA{m.ambito ? ` · ${AMBITO_LABEL[m.ambito] || ""}` : ""}</Badge>}
          {prio === "media" && <Badge className="bg-amber-500 text-white border-0 text-[10px] px-1.5 py-0">MEDIA</Badge>}
          <span className="text-[10px] text-slate-400 ml-auto">{timeLabel(m.created_at)}</span>
        </div>
        <div className="text-sm text-slate-800 whitespace-pre-wrap break-words">{m.text}</div>

        {isAlta && (
          <div className="mt-2 pt-2 border-t border-black/5">
            {(m.notify_emails?.length > 0) && (
              <div className="mb-1.5 text-[11px] text-slate-500 flex items-center gap-1">
                {m.escalated
                  ? <><CheckCircle2 className="h-3 w-3 text-emerald-600" /> Email di escalation inviata a {m.notify_emails.length} destinatario/i</>
                  : <><Clock className="h-3 w-3" /> Escalation email a {m.notify_emails.length} destinatario/i se non presa in carico in tempo</>}
              </div>
            )}
            {open && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-medium text-red-700 flex items-center gap-1"><AlertTriangle className="h-3.5 w-3.5" /> Da prendere in carico</span>
                <Button size="sm" variant="outline" className="h-7 text-xs" disabled={busyId === m.id} onClick={() => onStatus(m.id, "in_lavorazione")} data-testid={`chat-take-${m.id}`}>
                  {busyId === m.id ? <Loader2 className="h-3 w-3 animate-spin" /> : <><Clock className="h-3 w-3 mr-1" /> Prendo in carico</>}
                </Button>
                <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busyId === m.id} onClick={() => onStatus(m.id, "lavorata")} data-testid={`chat-done-${m.id}`}>
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Lavorata
                </Button>
              </div>
            )}
            {working && (
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-[11px] font-medium text-amber-700 flex items-center gap-1"><Clock className="h-3.5 w-3.5" /> In lavorazione da {m.taken_by_name}</span>
                <Button size="sm" className="h-7 text-xs bg-emerald-600 hover:bg-emerald-700 text-white" disabled={busyId === m.id} onClick={() => onStatus(m.id, "lavorata")} data-testid={`chat-done-${m.id}`}>
                  <CheckCircle2 className="h-3 w-3 mr-1" /> Lavorata
                </Button>
              </div>
            )}
            {done && (
              <span className="text-[11px] font-medium text-emerald-700 flex items-center gap-1"><CheckCircle2 className="h-3.5 w-3.5" /> Lavorata da {m.done_by_name}</span>
            )}
          </div>
        )}

        {/* Reazioni */}
        <div className="flex items-center gap-1 mt-1.5 flex-wrap">
          {Object.entries(m.reactions || {}).map(([emo, users]) => {
            const list = users || [];
            if (!list.length) return null;
            const reacted = list.includes(me);
            return (
              <button key={emo} type="button" onClick={() => onReact(m.id, emo)}
                data-testid={`chat-reaction-${m.id}-${emo}`}
                className={`inline-flex items-center gap-1 rounded-full border px-1.5 py-0.5 text-xs transition-colors ${reacted ? "border-emerald-300 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-600 hover:bg-slate-50"}`}>
                <span>{emo}</span><span className="font-medium">{list.length}</span>
              </button>
            );
          })}
          <Popover>
            <PopoverTrigger asChild>
              <button type="button" title="Aggiungi reazione" data-testid={`chat-react-btn-${m.id}`}
                className="inline-flex items-center justify-center h-6 w-6 rounded-full border border-slate-200 bg-white text-slate-400 hover:bg-slate-50 hover:text-slate-600 transition-colors">
                <SmilePlus className="h-3.5 w-3.5" />
              </button>
            </PopoverTrigger>
            <PopoverContent align={mine ? "end" : "start"} className="w-auto p-1.5" data-testid={`chat-react-picker-${m.id}`}>
              <div className="flex items-center gap-0.5">
                {REACTION_EMOJIS.map((emo) => (
                  <button key={emo} type="button" onClick={() => onReact(m.id, emo)}
                    data-testid={`chat-react-pick-${m.id}-${emo}`}
                    className="h-8 w-8 flex items-center justify-center text-xl rounded hover:bg-slate-100 transition-colors">
                    {emo}
                  </button>
                ))}
              </div>
            </PopoverContent>
          </Popover>
        </div>
      </div>
    </div>
  );
}

export default function ChatInterna() {
  const { subscribe, refreshUnread, soundEnabled, toggleSound } = useChat();
  const [params, setParams] = useSearchParams();
  const [meId, setMeId] = useState(null);
  const [conversations, setConversations] = useState([]);
  const [activeConv, setActiveConv] = useState(params.get("conv") || "staff");
  const [messages, setMessages] = useState([]);
  const [text, setText] = useState("");
  const [priority, setPriority] = useState("normale");
  const [ambito, setAmbito] = useState("contabilita");
  const [notifyOptions, setNotifyOptions] = useState([]);
  const [escalationMin, setEscalationMin] = useState(15);
  const [selectedNotify, setSelectedNotify] = useState(new Set());
  const [sending, setSending] = useState(false);
  const [busyId, setBusyId] = useState(null);
  const bottomRef = useRef(null);
  const inputRef = useRef(null);

  const activeMeta = conversations.find((c) => c.conv === activeConv);

  const loadContacts = useCallback(async () => {
    const { data } = await api.get("/chat/contacts");
    setMeId(data.me.user_id);
    setConversations(data.conversations);
  }, []);

  const loadMessages = useCallback(async (conv) => {
    const { data } = await api.get("/chat/messages", { params: { conv } });
    setMessages(data);
    refreshUnread();
    loadContacts();
  }, [refreshUnread, loadContacts]);

  useEffect(() => { loadContacts().catch(() => toast.error("Errore caricamento chat")); }, [loadContacts]);
  useEffect(() => {
    api.get("/chat/notify-options").then(({ data }) => {
      setNotifyOptions(data.options || []);
      setEscalationMin(data.escalation_minutes || 15);
    }).catch(() => {});
  }, []);
  useEffect(() => { loadMessages(activeConv).catch(() => {}); setParams({ conv: activeConv }); /* eslint-disable-line */ }, [activeConv]);
  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);

  useEffect(() => {
    const unsub = subscribe((evt) => {
      const m = evt.message;
      if (!m) return;
      if (m.conv === activeConv) {
        setMessages((prev) => {
          const i = prev.findIndex((x) => x.id === m.id);
          if (i >= 0) { const n = [...prev]; n[i] = m; return n; }
          return [...prev, m];
        });
        if (evt.type === "message") api.post("/chat/read", { conv: activeConv }).catch(() => {});
      }
      loadContacts();
    });
    return unsub;
  }, [subscribe, activeConv, loadContacts]);

  const send = async () => {
    const t = text.trim();
    if (!t) return;
    setSending(true);
    const body = { text: t, priority };
    if (activeMeta?.type === "dm") { body.conv_type = "dm"; body.peer_id = activeMeta.peer_id; }
    else body.conv_type = "room";
    if (priority === "alta") { body.ambito = ambito; body.notify_emails = [...selectedNotify]; }
    try {
      await api.post("/chat/messages", body);
      setText(""); setPriority("normale"); setSelectedNotify(new Set());
    } catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setSending(false); }
  };

  const onStatus = async (id, status) => {
    setBusyId(id);
    try { await api.post(`/chat/messages/${id}/status`, { status }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
    finally { setBusyId(null); }
  };

  const onReact = async (id, emoji) => {
    try { await api.post(`/chat/messages/${id}/react`, { emoji }); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const insertEmoji = (emo) => {
    const el = inputRef.current;
    if (!el) { setText((t) => t + emo); return; }
    const start = el.selectionStart ?? text.length;
    const end = el.selectionEnd ?? text.length;
    setText(text.slice(0, start) + emo + text.slice(end));
    requestAnimationFrame(() => {
      el.focus();
      const pos = start + emo.length;
      el.setSelectionRange(pos, pos);
    });
  };

  return (
    <div className="min-h-screen bg-slate-50">
      <Header />
      <div className="max-w-7xl mx-auto px-4 sm:px-6 py-6">
        <div className="mb-4 flex items-start justify-between gap-4">
          <div>
            <h1 className="font-heading text-2xl font-bold text-slate-900">Chat interna staff</h1>
            <p className="text-sm text-slate-500">Comunicazioni tra colleghi Assietica. Usa la priorità <b className="text-red-600">Alta</b> per contabilità ed emissione polizze: resta evidenziata finché non viene presa in carico.</p>
          </div>
          <div className="flex items-center gap-2 shrink-0">
          <button onClick={toggleSound} data-testid="chat-sound-toggle"
            className={`shrink-0 flex items-center gap-2 px-3 py-2 rounded-lg border text-sm transition-colors ${soundEnabled ? "border-emerald-200 bg-emerald-50 text-emerald-700" : "border-slate-200 bg-white text-slate-500"}`}
            title={soundEnabled ? "Avvisi sonori e notifiche attivi" : "Avvisi disattivati"}>
            {soundEnabled ? <Bell className="h-4 w-4" /> : <BellOff className="h-4 w-4" />}
            <span className="hidden sm:inline">{soundEnabled ? "Avvisi attivi" : "Avvisi off"}</span>
          </button>
          <ManualeButton route="/manuale/chat" />
          </div>
        </div>

        <div className="grid md:grid-cols-[280px_1fr] gap-4 h-[calc(100vh-220px)]">
          {/* Sidebar conversazioni */}
          <div className="rounded-xl border border-slate-200 bg-white overflow-y-auto" data-testid="chat-conv-list">
            {conversations.map((c) => {
              const active = c.conv === activeConv;
              return (
                <button key={c.conv} onClick={() => setActiveConv(c.conv)} data-testid={`chat-conv-${c.conv}`}
                  className={`w-full text-left px-3.5 py-3 border-b border-slate-100 flex items-center gap-3 transition-colors ${active ? "bg-slate-100" : "hover:bg-slate-50"}`}>
                  <div className={`h-9 w-9 rounded-full flex items-center justify-center shrink-0 ${c.type === "room" ? "bg-slate-900 text-white" : "bg-slate-200 text-slate-600"}`}>
                    {c.type === "room" ? <Hash className="h-4 w-4" /> : <Users className="h-4 w-4" />}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-1.5">
                      <span className="text-sm font-medium text-slate-800 truncate">{c.name}</span>
                      {c.alte_aperte > 0 && <Circle className="h-2 w-2 fill-red-500 text-red-500" />}
                    </div>
                    <div className="text-xs text-slate-400 truncate">{c.last ? `${c.last.sender_name}: ${c.last.text}` : "Nessun messaggio"}</div>
                  </div>
                  {c.unread > 0 && <span className="ml-1 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[11px] font-bold flex items-center justify-center">{c.unread}</span>}
                </button>
              );
            })}
          </div>

          {/* Pannello messaggi */}
          <div className="rounded-xl border border-slate-200 bg-white flex flex-col overflow-hidden">
            <div className="px-4 py-3 border-b border-slate-200 flex items-center gap-2">
              {activeMeta?.type === "room" ? <Hash className="h-4 w-4 text-slate-500" /> : <Users className="h-4 w-4 text-slate-500" />}
              <span className="font-medium text-slate-800">{activeMeta?.name || "Staff"}</span>
            </div>

            <div className="flex-1 overflow-y-auto p-4 space-y-3" data-testid="chat-messages">
              {messages.length === 0 ? (
                <div className="text-center text-slate-400 py-16 text-sm">Nessun messaggio. Scrivi il primo!</div>
              ) : messages.map((m) => (
                <MessageBubble key={m.id} m={m} me={meId} onStatus={onStatus} busyId={busyId} onReact={onReact} />
              ))}
              <div ref={bottomRef} />
            </div>

            {/* Composer */}
            <div className="border-t border-slate-200 p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-xs text-slate-500">Priorità:</span>
                {["normale", "media", "alta"].map((p) => (
                  <button key={p} type="button" onClick={() => setPriority(p)} data-testid={`chat-prio-${p}`}
                    className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs border transition-colors ${priority === p ? PRIO_META[p].ring + " font-semibold text-slate-800" : "border-slate-200 text-slate-500 hover:bg-slate-50"}`}>
                    <span className={`h-2 w-2 rounded-full ${PRIO_META[p].dot}`} /> {PRIO_META[p].label}
                  </button>
                ))}
                {priority === "alta" && (
                  <select value={ambito} onChange={(e) => setAmbito(e.target.value)} data-testid="chat-ambito"
                    className="text-xs border border-slate-300 rounded-md px-2 py-1 bg-white">
                    <option value="contabilita">Contabilità</option>
                    <option value="polizze">Emissione polizze</option>
                    <option value="altro">Altro</option>
                  </select>
                )}
              </div>
              {priority === "alta" && (
                <div className="rounded-lg border border-red-200 bg-red-50/60 px-3 py-2" data-testid="chat-notify-box">
                  <div className="text-[11px] text-red-700 font-medium flex items-center gap-1 mb-1.5">
                    <AlertTriangle className="h-3.5 w-3.5" /> Escalation email dopo {escalationMin} min senza presa in carico — seleziona i destinatari:
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-1">
                    {notifyOptions.map((o) => {
                      const checked = selectedNotify.has(o.email);
                      return (
                        <label key={o.email} className="flex items-center gap-2 text-xs text-slate-700 cursor-pointer" data-testid={`chat-notify-${o.email}`}>
                          <input type="checkbox" checked={checked} onChange={() => setSelectedNotify((prev) => {
                            const n = new Set(prev); n.has(o.email) ? n.delete(o.email) : n.add(o.email); return n;
                          })} className="rounded border-slate-300" />
                          <span className="truncate">{o.label}</span>
                        </label>
                      );
                    })}
                  </div>
                  {selectedNotify.size === 0 && <div className="text-[11px] text-slate-400 mt-1">Nessun destinatario selezionato: verrà inviato solo in chat (nessuna email).</div>}
                </div>
              )}
              <div className="flex items-end gap-2">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button type="button" variant="outline" className="h-10 w-10 p-0 shrink-0" title="Inserisci emoji" data-testid="chat-emoji-btn">
                      <Smile className="h-5 w-5 text-slate-500" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent align="start" className="w-64 p-2" data-testid="chat-emoji-picker">
                    <div className="grid grid-cols-8 gap-0.5 max-h-48 overflow-y-auto">
                      {EMOJIS.map((emo) => (
                        <button key={emo} type="button" onClick={() => insertEmoji(emo)}
                          data-testid={`chat-emoji-${emo}`}
                          className="h-8 w-8 flex items-center justify-center text-xl rounded hover:bg-slate-100 transition-colors">
                          {emo}
                        </button>
                      ))}
                    </div>
                  </PopoverContent>
                </Popover>
                <Textarea ref={inputRef} value={text} onChange={(e) => setText(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); send(); } }}
                  rows={2} placeholder="Scrivi un messaggio… (Invio per inviare, Shift+Invio a capo)"
                  className="resize-none" data-testid="chat-input" />
                <Button onClick={send} disabled={sending || !text.trim()} className="brand-gradient text-white border-0 h-10" data-testid="chat-send">
                  {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
